import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from '../ui';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import type { Rental } from '../config/supabase';
import { usePDFGenerator } from '../hooks/usePDFGenerator';
import SignaturePad, { SignaturePadRef } from './SignaturePad';

interface RentalContractProps {
  rental: Rental;
  isOpen: boolean;
  onClose: () => void;
  onSaveSignatures?: (customerSignature: string | null, shopSignature: string | null) => void;
}

const RentalContract: React.FC<RentalContractProps> = ({
  rental,
  isOpen,
  onClose,
  onSaveSignatures
}) => {
  const {
    loading,
    pdfUrl,
    generatePDF,
    downloadPDF,
    sendToWhatsApp,
    cleanup
  } = usePDFGenerator();

  const hasAutoGenerated = useRef(false);
  const signaturesChanged = useRef(false);
  const customerSignatureRef = useRef<SignaturePadRef>(null);
  const shopSignatureRef = useRef<SignaturePadRef>(null);
  const [hasCustomerSignature, setHasCustomerSignature] = useState(false);
  const [hasShopSignature, setHasShopSignature] = useState(false);

  const getSignatures = () => ({
    customerSignature: customerSignatureRef.current?.getDataURL() ?? null,
    shopSignature: shopSignatureRef.current?.getDataURL() ?? null,
  });

  const saveSignatures = () => {
    if (!signaturesChanged.current || !onSaveSignatures) return;
    const sigs = getSignatures();
    onSaveSignatures(sigs.customerSignature, sigs.shopSignature);
    signaturesChanged.current = false;
  };

  const handleCustomerSignatureChange = (has: boolean) => {
    setHasCustomerSignature(has);
    signaturesChanged.current = true;
  };

  const handleShopSignatureChange = (has: boolean) => {
    setHasShopSignature(has);
    signaturesChanged.current = true;
  };

  const handleRefreshPreview = () => {
    saveSignatures();
    generatePDF(rental, false, getSignatures());
  };

  const handleDownload = async () => {
    saveSignatures();
    const url = await generatePDF(rental, false, getSignatures());
    if (url) downloadPDF(rental, url);
  };

  const handleWhatsAppSend = async () => {
    saveSignatures();
    await generatePDF(rental, false, getSignatures());
    await sendToWhatsApp(rental, true);
  };

  const handleClose = () => {
    saveSignatures();
    onClose();
  };

  // Load saved signatures when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure canvas is mounted
      const timeoutId = setTimeout(() => {
        if (rental.customer_signature) {
          customerSignatureRef.current?.loadSignature(rental.customer_signature);
        }
        if (rental.shop_signature) {
          shopSignatureRef.current?.loadSignature(rental.shop_signature);
        }
        signaturesChanged.current = false;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, rental.customer_signature, rental.shop_signature]);

  // Auto-generate PDF when modal opens - only once per session
  useEffect(() => {
    if (isOpen && !hasAutoGenerated.current && !pdfUrl && !loading) {
      hasAutoGenerated.current = true;
      generatePDF(rental, false);
    } else if (!isOpen) {
      // Reset flag when modal closes
      hasAutoGenerated.current = false;
    }
  }, [isOpen, pdfUrl, loading, generatePDF, rental]);

  // Cleanup when modal actually closes
  useEffect(() => {
    if (!isOpen && pdfUrl) {
      // Delay cleanup to ensure modal has fully closed
      const timeoutId = setTimeout(() => {
        cleanup();
      }, 1000); // 1 second delay
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, pdfUrl, cleanup]);

  if (!rental) return null;

  const customer = rental.customers as any;
  const dress = rental.dresses as any;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Rental Contract"
      size="lg"
    >
      <div className="space-y-6">
        {/* Rental Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Contract Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Customer:</p>
              <p className="font-medium">{customer?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Phone:</p>
              <div className="flex items-center space-x-2">
                <p className="font-medium">{customer?.phone || 'N/A'}</p>
                {customer?.phone && customer.phone.replace(/\D/g, '').length >= 10 ? (
                  <span className="text-green-600 text-xs">✓ WhatsApp ready</span>
                ) : (
                  <span className="text-red-600 text-xs">⚠ No WhatsApp</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-gray-600">Dress:</p>
              <p className="font-medium">{dress?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Rental Price:</p>
              <p className="font-medium">${rental.rental_price?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-gray-600">Rental Date:</p>
              <p className="font-medium">{new Date(rental.rental_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' })}</p>
            </div>
            <div>
              <p className="text-gray-600">Return Date:</p>
              <p className="font-medium">{new Date(rental.expected_return_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' })}</p>
            </div>
            <div>
              <p className="text-gray-600">Location:</p>
              <p className="font-medium">{rental.location || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Alterations:</p>
              <p className="font-medium">
                {rental.alterations ? (
                  <span className={`px-2 py-1 rounded text-xs ${
                    rental.alterations === 'CLIENT' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {rental.alterations}
                  </span>
                ) : (
                  <span className="text-gray-500">Not specified</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Signature Pads */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-4">Signatures</h3>
          <div className="grid grid-cols-2 gap-6">
            <SignaturePad
              ref={customerSignatureRef}
              label="Customer Signature"
              onSignatureChange={handleCustomerSignatureChange}
            />
            <SignaturePad
              ref={shopSignatureRef}
              label="Shop Representative"
              onSignatureChange={handleShopSignatureChange}
            />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            {hasCustomerSignature && hasShopSignature
              ? '✓ Both signatures captured'
              : 'Sign above, then click "Refresh Preview" to embed signatures in PDF'}
          </p>
        </div>

        {/* PDF Preview */}
        {pdfUrl && isOpen && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Contract Preview</h4>
            <div className="relative">
                              <iframe
                  key={`pdf-${rental.id}`} // Stable key based on rental ID
                src={pdfUrl}
                className="w-full h-96 border border-gray-300 rounded"
                title="PDF Preview"
                onLoad={() => console.log('PDF loaded successfully')}
                onError={() => console.warn('PDF failed to load')}
                style={{ pointerEvents: 'auto' }}
              />
              {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                  <div className="text-gray-600">Loading PDF...</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
          >
            Close
          </Button>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleRefreshPreview}
              loading={loading}
              disabled={loading}
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              {pdfUrl ? 'Refresh' : 'Loading'} Preview
            </Button>
            
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={loading}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download
            </Button>
            
            <Button
              variant="primary"
              onClick={handleWhatsAppSend}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <ChatBubbleLeftEllipsisIcon className="h-4 w-4 mr-2" />
              Send via WhatsApp
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-3">
          
          <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800">
            <div className="flex items-start space-x-2">
              <ShareIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium mb-1">WhatsApp Instructions:</p>
                <div className="space-y-1">
                  <p>✅ PDF will be automatically downloaded to your device</p>
                  <p>✅ WhatsApp Web will open with the customer's number (if valid)</p>
                  <p>✅ Professional message will be pre-written</p>
                  <p>📎 Simply attach the downloaded PDF file to complete!</p>
                </div>
                                 {(!customer?.phone || customer.phone.replace(/\D/g, '').length < 10) && (
                   <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-yellow-800 text-xs">
                     ⚠️ Customer needs a valid phone number for WhatsApp integration
                   </div>
                 )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RentalContract; 